
import { apiUrls } from "@/environment/environment";
import { DxButton } from "devextreme-vue/button";
import {
  DxAsyncRule,
  DxColumn,
  DxDataGrid,
  DxEditing,
  DxEmailRule,
  DxFormat,
  DxItem,
  DxLookup,
  DxPaging,
  DxPatternRule,
  DxRequiredRule,
  DxScrolling,
  DxToolbar,
} from "devextreme-vue/data-grid";
import { DxSelectBox } from "devextreme-vue/select-box";
import { LoadOptions } from "devextreme/data/index";
import { Component, Vue } from "vue-property-decorator";
import IndexationController from "../../shared/services/controllers/indexation/indexationController";

import CustomStore from "devextreme/data/custom_store";
import DataSource from "devextreme/data/data_source";
import { ContentReadyEvent } from "devextreme/ui/select_box";

import { EditorPreparingEvent, InitNewRowEvent, OptionChangedEvent, RowValidatingEvent } from "devextreme/ui/data_grid";
import notify from "devextreme/ui/notify";
import { IndexationTiers, ParamsOptions, Societe, Tiers } from "../../shared/types/Indexation";

@Component({
  components: {
    DxDataGrid,
    DxColumn,
    DxPaging,
    DxEditing,
    DxLookup,
    DxToolbar,
    DxItem,
    DxButton,
    DxSelectBox,
    DxFormat,
    DxRequiredRule,
    DxEmailRule,
    DxPatternRule,
    DxAsyncRule,
    DxScrolling,
  },
})
export default class ClientsGrille extends Vue {
  private controller: IndexationController;

  constructor() {
    super();
    this.controller = new IndexationController(apiUrls.indexation);
  }

  selectedSociete: Societe | null = null;
  getSelectedSociete(): Societe | null {
    return this.selectedSociete;
  }

  selectedDisabled = false;
  handlePropertyChange(e: OptionChangedEvent): void {
    if (e.name === "editing") {
      this.selectedDisabled = (this.$refs.dxDataGrid as DxDataGrid)?.instance?.hasEditData() ?? false;
    }
  }

  storeClient = new CustomStore({
    key: "id",
    load: async (loadOptions) => {
      const requestOptions: ParamsOptions = { searchValue: loadOptions.searchValue, offset: loadOptions.skip, limit: loadOptions.take };
      let selectedSociete = this.getSelectedSociete();
      if (selectedSociete) {
        return await this.controller.indexationStore.getListeClients(selectedSociete, requestOptions).then((result) => {
          return {
            data: result.data,
            totalCount: result.itemsCount,
          };
        });
      }
    },
    insert: async (values) => {
      let selectedSociete = this.getSelectedSociete();
      if (selectedSociete) {
        await this.controller.indexationStore
          .addClient(values, selectedSociete)
          .then(() => {
            notify(
              {
                message: "Insert success",
                width: 400,
                type: "success",
                displayTime: 3500,
                animation: {
                  show: {
                    type: "fade",
                    duration: 400,
                    from: 0,
                    to: 1,
                  },
                  hide: { type: "fade", duration: 40, to: 0 },
                },
              },
              { position: "top right", direction: "down-push" }
            );
          })
          .catch((error) => {
            notify(
              {
                message: error,
                width: 400,
                type: "error",
                displayTime: 3500,
                animation: {
                  show: {
                    type: "fade",
                    duration: 400,
                    from: 0,
                    to: 1,
                  },
                  hide: { type: "fade", duration: 40, to: 0 },
                },
              },
              { position: "top right", direction: "down-push" }
            );
          });
      }
    },
    update: async (key, values) => {
      let client: IndexationTiers | undefined = undefined;

      // recuperation des data de la grille pour les fusionner avec les modifications
      const gridInstance = (this.$refs.dxDataGrid as DxDataGrid)?.instance;
      if (gridInstance) {
        const rowIndex: number = gridInstance.getRowIndexByKey(key);
        if (rowIndex > -1) {
          client = gridInstance.getVisibleRows()[rowIndex].data;
        }
      }
      // fusion des données avec les modifications
      client = { ...client, ...values };

      if (client) {
        await this.controller.indexationStore
          .updateClient(key, client)
          .then(() => {
            notify(
              {
                message: "Update success",
                width: 400,
                type: "success",
                displayTime: 3500,
                animation: {
                  show: {
                    type: "fade",
                    duration: 400,
                    from: 0,
                    to: 1,
                  },
                  hide: { type: "fade", duration: 40, to: 0 },
                },
              },
              { position: "top right", direction: "down-push" }
            );
          })
          .catch((err) => {
            notify(
              {
                message: err,
                width: 400,
                type: "error",
                displayTime: 3500,
                animation: {
                  show: {
                    type: "fade",
                    duration: 400,
                    from: 0,
                    to: 1,
                  },
                  hide: { type: "fade", duration: 40, to: 0 },
                },
              },
              { position: "top right", direction: "down-push" }
            );
          });
      }
    },
    remove: async (key) => {
      await this.controller.indexationStore
        .removeClient(key)
        .then(() => {
          notify(
            {
              message: "Delete success",
              width: 400,
              type: "success",
              displayTime: 3500,
              animation: {
                show: {
                  type: "fade",
                  duration: 400,
                  from: 0,
                  to: 1,
                },
                hide: { type: "fade", duration: 40, to: 0 },
              },
            },
            { position: "top right", direction: "down-push" }
          );
        })
        .catch((err) => {
          notify(
            {
              message: err,
              width: 400,
              type: "error",
              displayTime: 3500,
              animation: {
                show: {
                  type: "fade",
                  duration: 400,
                  from: 0,
                  to: 1,
                },
                hide: { type: "fade", duration: 40, to: 0 },
              },
            },
            { position: "top right", direction: "down-push" }
          );
        });
    },
  });

  storeSociete = new CustomStore({
    key: "id",
    load: async (loadOptions) => {
      return await this.controller.indexationStore.getListeSocietes().then((result) => {
        return {
          data: result.data,
          totalCount: result.itemsCount,
        };
      });
    },
  });

  dataSourceClient = new DataSource({
    store: this.storeClient,
    paginate: true,
    pageSize: 20,
  });

  dataSourceSociete = new DataSource({
    store: this.storeSociete,
  });

  dataSourceIndexFamily = {
    store: new CustomStore({
      key: "id",
      load: async () => {
        return await this.controller.indexationStore
          .getListeIndexFamily()
          .then((result) => {
            return {
              data: result.data,
              totalCount: result.itemsCount,
            };
          })
          .catch(() => {
            throw new Error("Network error");
          });
      },

      byKey: async (key) => {
        return await this.controller.indexationStore
          .getListeIndexFamily()
          .then((result) => {
            const IndexFamily = result.data.find((i) => i.id === key);
            return IndexFamily;
          })
          .catch(() => {
            throw new Error("Network error");
          });
      },
    }),
  };

  selectSocieteChanged() {
    let dxDataGrid = this.$refs.dxDataGrid as DxDataGrid;
    dxDataGrid?.instance?.refresh();
    this.refreshTiersDO();
  }

  initialized = false;
  onContentReady(e: ContentReadyEvent) {
    if (!this.initialized) {
      this.initialized = true;

      e.component
        .getDataSource()
        .load()
        .then((res: Societe[]) => {
          const currentCompanyIndex = res.findIndex((c) => c.code === this.$store.state.userContext.currentCompany);
          e.component.option("value", res[currentCompanyIndex ?? 0]);
        });
    }
  }

  onEditorPreparing(e: EditorPreparingEvent): void {
    // Enable la date en edition
    if ((e.dataField == "tiersId" || e.dataField == "indexFamilyId") && e.parentType == "dataRow") {
      e.editorOptions.disabled = !e.row?.isNewRow;
    }
  }

  getTiersCodeName(e: Tiers) {
    return `${e.code} - ${e.nom}`;
  }

  initNewRow(e: InitNewRowEvent) {
    // initialisation de la date a date du jours lors de l'ajout de ligne
    e.data.date = new Date();
  }

  dataSourceTiers = {
    store: new CustomStore({
      key: "id",
      load: async (loadOptions: LoadOptions) => {
        const requestOptions: ParamsOptions = { searchValue: loadOptions.searchValue, offset: loadOptions.skip, limit: loadOptions.take };
        return this.getListeTiers(requestOptions);
      },
      byKey: async (key) => {
        return this.getTiers(key);
      },
    }),
    paginate: true,
    pageSize: 20,
  };

  /* Debut clients */
  getTiers(tiersId: number): Tiers | undefined {
    let data: Tiers | undefined = undefined;
    data = this.TiersDO.find((i: Tiers) => i.id === tiersId);
    return data;
  }

  getListeTiers(requestOptions: ParamsOptions): { data: Tiers[]; itemsCount: number } {
    let data = [] as Tiers[];
    const offset = requestOptions.offset ?? 0;
    const limit = requestOptions.limit ?? 20;
    const searchValue = requestOptions.searchValue ?? "";
    let itemsCount = 0;
    data = this.TiersDO.sort(this.compareTiers)
      .filter((t) => `${t.code} - ${t.nom}`.toLowerCase().includes(searchValue.toLowerCase()))
      .slice(offset, offset + limit);
    itemsCount = data.length;
    return { data, itemsCount };
  }

  compareTiers(t1: Tiers, t2: Tiers) {
    const chaineT1 = t1.code.toLowerCase(); // ignore upper and lowercase
    const chaineT2 = t2.code.toLowerCase(); // ignore upper and lowercase
    if (chaineT1 < chaineT2) {
      return -1;
    }
    if (chaineT1 > chaineT2) {
      return 1;
    }
    // names must be equal
    return 0;
  }

  CustomerName(e: IndexationTiers) {
    const tiers = this.TiersDO.find((i: Tiers) => i.id === e.tiersId);
    return `${tiers?.code} - ${tiers?.nom}`;
  }

  TiersDO: Tiers[] = [];

  async refreshTiersDO() {
    let selectedSociete = this.getSelectedSociete();
    if (selectedSociete) {
      return await this.controller.indexationStore
        .getListeTiers(selectedSociete)
        .then((result) => {
          this.TiersDO = result.data;
        })
        .catch(() => {
          throw new Error("Network error");
        });
    }
  }

  /**
   * Validation de donnees avant insertion
   * @param e
   */
  onRowValidating(e: RowValidatingEvent) {
    let selectedSociete = this.getSelectedSociete();
    if (e.newData && selectedSociete) {
      e.promise = this.controller.indexationStore
        .validateNewTiers(e.newData, selectedSociete)
        .then((result: { errorText: string; isValid: boolean }) => {
          e.errorText = result.errorText;
          e.isValid = result.isValid;
        })
        .catch((error) => {
          e.errorText = error;
          e.isValid = false;
        });
    }
  }

  beforeMount() {
    this.initialized = false;
  }
}
